import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Snowflake, Clock, Leaf, ArrowRight, CheckCircle, Thermometer, Package, TrendingUp, Droplets, Zap, BarChart3, AlertTriangle, MapPin, Truck, FileText, Gauge } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import UnifiedContactModal from '../components/UnifiedContactModal';

const HydrocoolersPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const features = [
    {
      icon: Snowflake,
      title: 'Rapid Field Heat Removal',
      description: 'Remove 40°F / ~22°C of field heat in minutes instead of hours with water-based cooling'
    },
    {
      icon: Clock,
      title: 'Minutes Matter',
      description: 'Meet time-to-cool targets of 2–4 hours from harvest to reach optimal pulp temperature'
    },
    {
      icon: Leaf,
      title: 'Quality Preservation',
      description: 'Stops respiration, prevents breakdown, and extends shelf life by days'
    }
  ];

  const coldChainSteps = [
    {
      number: '1',
      title: 'Harvest',
      description: 'Fruit picked on a warm day can be 80–90°F inside the bin'
    },
    {
      number: '2',
      title: 'Portable Hydrocooling',
      description: 'Rapidly drops pulp temperature below 41°F / 5°C within hours of harvest',
      highlight: true
    },
    {
      number: '3',
      title: 'Packing House',
      description: 'Pre-cooled fruit is easier to handle, sort, and pack'
    },
    {
      number: '4',
      title: 'Cold Storage',
      description: "Maintains quality — can't fix what wasn't cooled properly"
    },
    {
      number: '5',
      title: 'Transport',
      description: 'Loaded into reefer trucks or containers, holding temp steady until delivery'
    }
  ];

  const operationalScenarios = [
    {
      icon: MapPin,
      title: 'Remote Ranches or Long Truck Hauls',
      points: [
        'Place a portable tunnel or batch hydrocooler at the ranch or field collection center',
        'Cool fruit from ~80°F / 27°C down into the 30s°F / low single digits °C before it ever hits the road',
        'Reduce the load on the main packhouse tunnel, which only needs to trim temperature to final target'
      ]
    },
    {
      icon: Thermometer,
      title: 'Hot Midday Picking',
      points: [
        'Use portable hydrocooling at shaded field yards to strip out field heat from the hottest loads',
        'Protect stem and fruit quality on days when pulp temperature would otherwise spike',
        'Maintain consistent quality even during peak heat conditions'
      ]
    },
    {
      icon: Truck,
      title: 'Export and Long Storage Programs',
      points: [
        'Use portable hydrocooling as the first deep cooling step before long storage or export',
        'Help fruit reach just above its freezing point quickly, then maintain that temperature through the rest of the cold chain',
        'Maximize shelf life for premium markets'
      ]
    },
    {
      icon: Package,
      title: 'Mixed Varieties and Smaller Lots',
      points: [
        'Use portable batch hydrocoolers with multiple zones to handle small, high-value lots without disrupting a main tunnel',
        'Offer differentiated handling for premium programs, organic blocks, or specific customers',
        'Flexible cooling for trial blocks and specialty varieties'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Portable Hydrocoolers | Cherry Hydrocoolers & Post-Harvest Cooling Systems"
        description="Portable tunnel and batch hydrocoolers that remove field heat fast. Meet time-to-cool targets at remote ranches and field yards for cherries and high-value crops."
        canonicalPath="/hydrocoolers"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 via-ctc-silver-light to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Snowflake size={16} />
              <span>Portable Hydrocooling Solutions</span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Hydrocoolers: The First and Most Powerful Step in Your Cold Chain
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-6">
              Portable tunnel and batch hydrocoolers that pull out field heat fast, so your fruit hits cold storage at the right temperature, right on time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="text-green-500" size={20} />
                <span className="font-medium">Remove 40°F / ~22°C of field heat in minutes instead of hours</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <CheckCircle className="text-green-500" size={20} />
                <span className="font-medium">Meet time-to-cool targets even at remote ranches and field yards</span>
              </div>
            </div>
            <motion.button
              onClick={() => setIsContactModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg inline-flex items-center space-x-2"
            >
              <span>Request Hydrocooler Quote</span>
              <ArrowRight size={20} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* What is a Hydrocooler */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16"
          >
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  What is a Hydrocooler?
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  A hydrocooler is a postharvest cooling system that rapidly removes field heat from freshly harvested cherries and other crops by using cold water.
                  Instead of relying on air cooling — which is slower and less consistent — a hydrocooler showers
                  the product with cold water that transfers heat far more efficiently.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  This critical first step in the cold chain stops fruit respiration, prevents mold growth,
                  and significantly extends shelf life — directly impacting your bottom line and market reach.
                </p>
              </div>

              <div className="space-y-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-4"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                      <feature.icon className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Product Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 rounded-2xl p-8 shadow-xl">
                <img
                  src="/Portable.jpg"
                  alt="CTC Waterworks portable hydrocoolers for cherry cooling in agricultural field setting"
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>

              {/* Performance Badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl p-4 border border-blue-200">
                <div className="text-2xl font-bold text-blue-600">25x</div>
                <div className="text-gray-600 text-sm">Faster Than Air Cooling</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Minutes After Harvest Matter */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Why Minutes After Harvest Matter
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every operation talks about "the cold chain," but the real differentiator is how quickly fruit actually gets cold the first time.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-lg flex items-center justify-center mb-4">
                <Clock className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Time Burns Shelf Life
              </h3>
              <p className="text-gray-600">
                Every hour fruit stays warm after harvest burns shelf life you cannot get back later in storage. The clock starts ticking the moment fruit leaves the tree.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Thermometer className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Science-Backed Targets
              </h3>
              <p className="text-gray-600">
                Research and industry guidelines show that getting pulp temperature below about 41°F / 5°C within 4 hours of harvest, then down toward 32–34°F / 0–1°C, can add days of usable life for cherries and other heat-sensitive crops.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Droplets className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Water is the Solution
              </h3>
              <p className="text-gray-600">
                Water-based hydrocooling is the only practical way to pull 40°F+ / ~22°C of field heat out of fruit in minutes instead of hours. Air cooling simply can't match this speed.
              </p>
            </motion.div>
          </div>

          {/* Blog Post Callout */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-6 text-white shadow-lg"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="text-blue-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">
                  Want the deeper technical dive?
                </h3>
                <p className="mb-4 text-white/90">
                  Learn how early hydrocooling protects cherry quality, the science behind time-to-cool targets, and what happens when cooling is delayed.
                </p>
                <Link
                  to="/blog/minutes-matter-why-early-hydrocooling-makes-or-breaks-cherry-quality"
                  className="inline-flex items-center space-x-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  <span>Read: "Minutes Matter: Why Early Hydrocooling Makes or Breaks Cherry Quality"</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Time and Temperature Targets Callout */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-cyan-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-300 shadow-xl"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
                <Gauge className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Time and Temperature Targets
              </h3>
              <p className="text-gray-600">
                Industry best practices for field heat removal and postharvest cooling
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="text-gray-800 font-medium">Remove field heat within 2–4 hours of harvest wherever possible</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="text-gray-800 font-medium">Cool pulp temperature below 41°F / 5°C in that window</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="text-gray-800 font-medium">Continue cooling toward 32–34°F / 0–1°C for storage and shipment</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="text-gray-800 font-medium">Maintain high relative humidity in holding areas to reduce water loss and stem shrivel</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Portable Hydrocoolers for the First Miles */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Portable Hydrocoolers for the First Miles of Your Cold Chain
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              All CTC hydrocoolers can be built as portable units, designed to sit where they add the most value: at the ranch, in field collection yards, or as satellite cooling at remote sites. Portable hydrocooling is often the simplest way to meet time-to-cool targets.
            </p>
          </motion.div>

          {/* Portable Tunnel Hydrocoolers */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-blue-200">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                    <Zap size={16} />
                    <span>High Throughput</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Portable Tunnel Hydrocoolers
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Conveyorized, high-throughput portable hydrocoolers designed for continuous-flow operations.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Continuous-flow hydrocooling with bins conveyed through the tunnel</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Best suited for large ranches, centralized field yards, or high-volume packhouse integration</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Ideal when you want steady bin traffic and consistent cooling times</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Can be deployed as field-side units during harvest and then used as part of the main packhouse system the rest of the season</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
                  <img
                    src="/Website copy.jpg"
                    alt="Portable tunnel hydrocooler units in field yard for cherry cooling"
                    className="w-full h-64 object-cover rounded-lg shadow-lg"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Portable Batch Hydrocoolers */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-blue-200">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
                  <img
                    src="/Batch Model.jpg"
                    alt="Portable batch hydrocooler system for flexible cooling"
                    className="w-full h-64 object-cover rounded-lg shadow-lg"
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <div className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                    <Package size={16} />
                    <span>Flexible & Zoned</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Portable Batch Hydrocoolers
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Zoned batch units that cool stacks of bins at a time, also available as portable skids or frames.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Individually controlled zones to run different varieties, blocks, or customers in parallel</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Lower capital cost than a full conveyor tunnel</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Highly flexible for mixed varieties, smaller lots, trial blocks, and premium programs</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Easy to place at satellite yards or secondary sites to relieve bottlenecks</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stationary Option */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-blue-200">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center space-x-2 bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                    <Package size={16} />
                    <span>High Volume Packhouse</span>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">
                    Stationary Hydrocoolers
                  </h4>
                  <p className="text-gray-600 mb-6">
                    Any of the above designs can be configured as stationary packhouse hydrocoolers when portability is not required. High-volume models designed for the main packing line receiving station, integrated into existing layouts for permanent installations.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Permanent installation at main packhouse receiving</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Integrated with existing packing line infrastructure</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">Optimized for continuous high-volume operations</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
                  <img
                    src="/Tunnel.jpg"
                    alt="Stationary tunnel hydrocooler system for high-volume packhouse operations"
                    className="w-full h-64 object-cover rounded-lg shadow-lg"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Where Hydrocoolers Fit in Your Operation */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Where Hydrocoolers Fit in Your Operation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The question is not just "which hydrocooler," but "where should hydrocooling live in your harvest and packing flow."
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {operationalScenarios.map((scenario, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 shadow-md"
              >
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <scenario.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {scenario.title}
                    </h3>
                  </div>
                </div>
                <ul className="space-y-3">
                  {scenario.points.map((point, pointIndex) => (
                    <li key={pointIndex} className="flex items-start space-x-2">
                      <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={16} />
                      <span className="text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Size a Hydrocooler */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              How We Size a Hydrocooler
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When we design a hydrocooling system, we start with your operation, not a catalog number. Key inputs include:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Volume Requirements
              </h3>
              <p className="text-gray-600">
                Peak bins per hour or tons per hour you need to handle at harvest
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Thermometer className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Temperature Conditions
              </h3>
              <p className="text-gray-600">
                Typical and worst-case field temperatures during your harvest season
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Clock className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Time-to-Cool Targets
              </h3>
              <p className="text-gray-600">
                Target pulp temperature and allowed time from harvest to first cooling
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Site Conditions
              </h3>
              <p className="text-gray-600">
                Available space and utilities at the ranch, yard, or packhouse
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Truck className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Portability Needs
              </h3>
              <p className="text-gray-600">
                Whether you need the hydrocooler to be portable, stationary, or both over the course of the season
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Package className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Crop Variety Mix
              </h3>
              <p className="text-gray-600">
                Different varieties, block sizes, and premium program requirements
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-8 shadow-lg border border-blue-200 text-center"
          >
            <p className="text-lg text-gray-700 mb-6">
              From there, we match you to a tunnel or batch configuration and coordinate with your refrigeration provider to help you hit your time-to-cool targets.
            </p>
            <motion.button
              onClick={() => setIsContactModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg inline-flex items-center space-x-2"
            >
              <span>Share your layout and volumes – we'll propose a portable hydrocooling plan</span>
              <ArrowRight size={20} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Cold Chain Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Where Hydrocooling Fits in the Cold Chain
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              For cherry growers in the Pacific Northwest, the cold chain is the step-by-step temperature management process from orchard to consumer.
              <strong className="text-blue-600"> Hydrocooling is ALWAYS Step 1.</strong>
            </p>
          </motion.div>

          <div className="relative mb-16">
            {/* Connecting Line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 transform -translate-y-1/2 hidden md:block"></div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {coldChainSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative ${step.highlight ? 'z-10' : ''}`}
                >
                  <div className={`
                    ${step.highlight
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg transform md:-translate-y-4'
                      : 'bg-white text-gray-800 border border-gray-200'}
                    rounded-xl p-6 h-full transition-all duration-300 hover:shadow-md relative
                  `}>
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold mb-4 mx-auto
                      ${step.highlight
                        ? 'bg-white text-blue-600'
                        : 'bg-blue-100 text-blue-600'}
                    `}>
                      {step.number}
                    </div>
                    <h3 className={`text-xl font-semibold mb-3 text-center ${step.highlight ? 'text-white' : 'text-gray-900'}`}>
                      {step.title}
                    </h3>
                    <p className={`text-center ${step.highlight ? 'text-white/90' : 'text-gray-600'}`}>
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-red-50 rounded-xl p-6 border border-red-200 mb-12"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-red-800 mb-3">
                  Without Proper Hydrocooling:
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <span className="text-red-600 text-xs">✕</span>
                    </div>
                    <span className="text-red-700">Internal fruit temperatures stay high → accelerating mold growth, softening, and breakdown</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <span className="text-red-600 text-xs">✕</span>
                    </div>
                    <span className="text-red-700">Cooling later with forced air takes much longer and often won't reach the core</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <span className="text-red-600 text-xs">✕</span>
                    </div>
                    <span className="text-red-700">Shorter shelf life = lost profit and reduced market reach</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Time and Temperature Targets */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-cyan-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-300 shadow-xl"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
                <Gauge className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Time and Temperature Targets
              </h3>
              <p className="text-gray-600">
                Effective cold chain management starts with what happens in the first few hours after harvest
              </p>
            </div>
            <div className="space-y-4 max-w-3xl mx-auto">
              <p className="text-gray-700">
                As general hydrocooling guidelines:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-gray-800 font-medium">Aim to remove field heat within 2–4 hours of harvest wherever possible</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-gray-800 font-medium">Cool pulp temperature below about 41°F / 5°C within that window</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-gray-800 font-medium">Continue cooling toward 32–34°F / 0–1°C for storage and shipping</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <span className="text-gray-800 font-medium">Keep relative humidity high in holding and staging areas to reduce water loss and stem shrivel</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 text-center pt-4">
                Hydrocoolers are the most efficient way to hit these time-and-temperature targets, especially for heat-sensitive crops like cherries.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ideal Crops */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Who Needs a Hydrocooler?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              For over 40 years, Pacific Northwest cherry growers have trusted CTC Waterworks for their cherry hydrocoolers — cherries benefit dramatically from rapid hydrocooling, with measurable quality and shelf-life improvements. The same technology also benefits many other high-value crops.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              { name: 'Cherries', icon: '🍒', highlight: true, link: '/cherry-hydrocoolers' },
              { name: 'Berries', icon: '🫐' },
              { name: 'Stone Fruit', icon: '🍑' },
              { name: 'Sweet Corn', icon: '🌽' },
              { name: 'Leafy Greens', icon: '🥬' },
              { name: 'Asparagus', icon: '🌱' },
              { name: 'Grapes', icon: '🍇' },
              { name: 'Other Delicate Crops', icon: '🥦' }
            ].map((crop, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className={`text-center p-6 rounded-lg border ${
                  crop.highlight
                    ? 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-300 shadow-md'
                    : 'bg-white border-blue-100'
                }`}
              >
                {crop.link ? (
                  <Link to={crop.link} className="block h-full w-full">
                    <div className="text-4xl mb-3">{crop.icon}</div>
                    <div className={`font-medium ${crop.highlight ? 'text-blue-800' : 'text-gray-900'} hover:text-ctc-blue transition-colors`}>
                      {crop.name}
                    </div>
                  </Link>
                ) : (
                  <>
                    <div className="text-4xl mb-3">{crop.icon}</div>
                    <div className={`font-medium ${crop.highlight ? 'text-blue-800' : 'text-gray-900'}`}>{crop.name}</div>
                  </>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-6 shadow-lg border border-blue-200"
          >
            <div className="text-center">
              <p className="text-gray-700 italic mb-4">
                "Any crop that is delicate, high-value, and suffers from high respiration and moisture loss can benefit from hydrocooling."
              </p>
              <p className="text-gray-700">
                For a cherry-specific deep dive, visit our{' '}
                <Link to="/cherry-hydrocoolers" className="text-blue-600 hover:text-blue-700 font-medium underline">
                  Cherry Hydrocoolers for Premium Cold Chain Management
                </Link>{' '}
                page.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Trusted by Leading PNW Cherry Growers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              For decades, the region's top cherry producers have relied on CTC Waterworks hydrocoolers to maximize quality and shelf life.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <motion.a
              href="https://washfruitgrowers.com/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
            >
              <img
                src="/dealer-logos/washington-fruit.png"
                alt="Washington Fruit"
                className="max-h-full max-w-full object-contain"
                loading="eager"
                onError={(e) => {
                  e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-red-600">WASHINGTON FRUIT</div>';
                }}
              />
            </motion.a>

            <motion.a
              href="https://rainierfruit.com/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
            >
              <img
                src="/dealer-logos/rainier-fruit.png"
                alt="Rainier Fruit"
                className="max-h-full max-w-full object-contain"
                loading="eager"
                onError={(e) => {
                  e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-red-600">RAINIER FRUIT</div>';
                }}
              />
            </motion.a>

            <motion.a
              href="https://allanbrosfruit.com/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
            >
              <img
                src="/dealer-logos/allan-bros.png"
                alt="Allan Brothers"
                className="max-h-full max-w-full object-contain"
                loading="eager"
                onError={(e) => {
                  e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-red-600">ALLAN BROTHERS</div>';
                }}
              />
            </motion.a>

            <motion.a
              href="https://gebbersfarms.com/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-200 flex items-center justify-center h-32"
            >
              <img
                src="/dealer-logos/gebbers-farms.png"
                alt="Gebbers Farms"
                className="max-h-full max-w-full object-contain"
                loading="eager"
              />
            </motion.a>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-8 shadow-lg border border-blue-200 mb-12"
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              Refrigeration Partners
            </h3>
            <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
              We work closely with these trusted refrigeration companies, but are happy to collaborate with any refrigeration provider you prefer.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <motion.a
                href="https://versatilerefrigeration.com/"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 flex items-center justify-center h-24"
              >
                <img
                  src="/dealer-logos/versatile-refrigeration.jpg"
                  alt="Versatile Refrigeration"
                  className="max-h-full max-w-full object-contain"
                  loading="eager"
                  onError={(e) => {
                    e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-blue-600">VERSATILE REFRIGERATION</div>';
                  }}
                />
              </motion.a>

              <motion.a
                href="https://www.cwrcentral.com/"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 flex items-center justify-center h-24"
              >
                <img
                  src="/dealer-logos/cwr.png"
                  alt="Central Washington Refrigeration"
                  className="max-h-full max-w-full object-contain"
                  loading="eager"
                  onError={(e) => {
                    e.currentTarget.parentElement!.innerHTML = '<div class="font-bold text-lg text-blue-600">CENTRAL WASHINGTON REFRIGERATION</div>';
                  }}
                />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Why This is a Smart Investment
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A properly designed hydrocooler delivers measurable returns on your investment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Reduced Shrink
              </h3>
              <p className="text-gray-600">
                Pays for itself in reduced shrink and higher pack-out percentages, directly improving your bottom line.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <Clock className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Extended Shelf Life
              </h3>
              <p className="text-gray-600">
                Extends shelf life by several days, unlocking higher prices and access to more distant markets.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <Package className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Market Flexibility
              </h3>
              <p className="text-gray-600">
                Gives packers the flexibility to store, pack, or ship when market conditions are best, not when forced by product deterioration.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 rounded-2xl p-8 text-white shadow-xl"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl font-bold mb-4">Key Takeaway</h3>
              <p className="text-xl mb-6 leading-relaxed">
                "For Pacific Northwest cherry growers and packers, portable hydrocoolers are the critical first link in the cold chain — if you lose quality at this step,
                you can't get it back later. Rapid, uniform cooling is what keeps cherries firm, sweet, and saleable for top dollar in domestic and export markets, especially for premium Asian exports."
              </p>
              <motion.button
                onClick={() => setIsContactModalOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center space-x-2 shadow-lg"
              >
                <span>Request Hydrocooler Quote</span>
                <ArrowRight size={20} />
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-16 text-center"
          >
            <p className="text-gray-600 mb-4">
              With over 40 years of experience serving the Pacific Northwest cherry industry, CTC Waterworks is THE trusted expert in hydrocooling technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Unified Contact Modal */}
      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="hydrocoolers"
        formSource="product_quote"
      />

      <Footer />
    </div>
  );
};

export default HydrocoolersPage;
